
import React, { useState, useRef } from 'react';
import { PERFORMANCE_DATA } from '../constants';
import { Language } from '../types';
import { translations } from '../translations';

interface PerformanceProps {
  lang: Language;
}

const Performance: React.FC<PerformanceProps> = ({ lang }) => {
  const [showAll, setShowAll] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const t = translations[lang].performance;
  const dataToShow = showAll ? PERFORMANCE_DATA : PERFORMANCE_DATA.slice(0, 5);

  const toggleShowAll = () => {
    if (showAll) {
      const offset = 80;
      const elementPosition = sectionRef.current?.getBoundingClientRect().top ?? 0;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      setTimeout(() => setShowAll(false), 100);
    } else {
      setShowAll(true);
    }
  };

  return (
    <section ref={sectionRef} id="performance" className="py-32 bg-black relative">
      <div className="max-w-7xl mx-auto px-6">
        <header className="text-center mb-20">
          <h2 className="text-5xl md:text-7xl font-bold mb-8 tracking-tighter text-white">{t.title}</h2>
          <p className="text-gray-400 text-xl md:text-2xl font-light">{t.subtitle}</p>
        </header>

        <div className="overflow-x-auto rounded-[32px] border border-white/10 bg-white/[0.01] shadow-2xl">
          <table className="w-full border-collapse min-w-[1100px] table-fixed" aria-label="Historial de rendimiento de AxiomityFX">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.02] text-gray-500 text-[11px] uppercase tracking-[0.3em] font-black">
                <th scope="col" className="py-8 px-8 text-left w-24">{t.year}</th>
                {t.months.map(m => <th scope="col" key={m} className="py-8 px-1 text-center">{m}</th>)}
                <th scope="col" className="py-8 px-8 text-right w-40">{t.total}</th>
              </tr>
            </thead>
            <tbody>
              {dataToShow.map((row) => (
                <tr key={row.year} className="group border-b border-white/5 transition-all duration-500 hover:bg-sky-500/[0.04]">
                  <th scope="row" className="py-7 px-8 text-base font-black text-gray-400 group-hover:text-white transition-colors relative">
                    <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-sky-500 scale-y-0 group-hover:scale-y-100 transition-transform duration-500 origin-top"></div>
                    {row.year}
                  </th>
                  {translations.es.performance.months.map((m_es, idx) => {
                    const value = row.months[m_es];
                    const isPositive = value > 0;
                    const isNegative = value < 0;
                    return (
                      <td key={idx} className="py-7 px-1 text-center">
                        {value !== undefined ? (
                          <span className={`text-base font-bold transition-all duration-500 group-hover:scale-110 inline-block ${isPositive ? "text-emerald-400" : isNegative ? "text-red-400" : "text-gray-500"}`}>
                            {isPositive ? "+" : ""}{value.toFixed(1)}%
                          </span>
                        ) : <span className="text-gray-800 text-sm">-</span>}
                      </td>
                    );
                  })}
                  <td className="py-7 px-8 text-right transition-all duration-500 group-hover:translate-x-[-4px]">
                    <span className="font-black text-lg text-sky-400 drop-shadow-[0_0_10px_rgba(14,165,233,0.3)]">+{row.total.toFixed(1)}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-14 text-center">
          <button onClick={toggleShowAll} className="group relative px-10 py-4 rounded-full border border-white/10 text-sm font-black uppercase tracking-[0.2em] text-gray-500 hover:text-white hover:border-sky-500/50 transition-all overflow-hidden">
            <span className="relative z-10 flex items-center gap-3">
              {showAll ? <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 15l7-7 7 7" /></svg>{t.collapse}</> : <>{t.expand}<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" /></svg></>}
            </span>
            <div className="absolute inset-0 bg-sky-500/10 translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
          </button>
        </div>
        <article className="mt-20 p-8 md:p-10 rounded-[32px] bg-white/[0.02] border border-white/5 max-w-4xl mx-auto backdrop-blur-md">
          <p className="text-xs text-gray-500 leading-relaxed font-medium text-center">{t.disclaimer}</p>
        </article>
      </div>
    </section>
  );
};

export default Performance;
